import { parseISO } from '@flex-packages/date';
import { unregisterFCM } from '@flex-packages/firebase/utils';
import { COOKIE_KEY_LOCALE } from '@flex-packages/i18n-base';
import { deleteDB } from '@flex-packages/indexed-db';
import { disconnect } from '@flex-packages/server-push-consumer';
import globalAxios from 'axios';
import Cookie from 'universal-cookie';
import './apis/auth-login-flow/apis/account-access-control-setting-api-controller-api.mjs';
import './apis/auth-login-flow/apis/customer-user-access-api-controller-api.mjs';
import './apis/auth-login-flow/apis/ip-resolve-controller-api.mjs';
import './apis/auth-login-flow/apis/login-flow-api-controller-api.mjs';
import './apis/auth-login-flow/apis/login-flow-authentication-api-controller-api.mjs';
import './apis/auth-login-flow/apis/login-flow-authorization-api-controller-api.mjs';
import './apis/auth-login-flow/apis/login-flow-post-authentication-api-controller-api.mjs';
import './apis/auth-login-flow/apis/login-flow-verification-api-controller-api.mjs';
import { logoutRemote } from './apis/auth-login-flow/apis/workspace-access-token-api-controller-api.mjs';
import { broadcastUnauthorizedMessage } from './broadcast.mjs';
import { invalidateSessionStorage, invalidateLocalStorage } from './clearBrowserStorage.mjs';
import { COOKIE_KEY_AID, SECURED_COOKIE_OPTIONS, DEFAULT_COOKIE_OPTIONS, COOKIE_KEY_VERSION_IDENTIFIER, COOKIE_VALUE_V2_VERSION_IDENTIFIER, COOKIE_KEY_WS_RID, COOKIE_KEY_WS_AID, COOKIE_KEY_LOGIN_ERROR_MESSAGE, COOKIE_KEY_WS_FFA, COOKIE_KEY_DEVICE_ID, COOKIE_MAX_AGE, COOKIE_KEY_CURRENT_CUSTOMER_INFO, HEADER_KEY_DEVICE_ID } from './constants.mjs';

const LOGIN_PAGE_PATH = "/auth/login";
function setCustomerUserAccessToken(customerUserAccessToken, stage, cookie = new Cookie()) {
  cookie.set(COOKIE_KEY_AID, customerUserAccessToken.token, {
    ...DEFAULT_COOKIE_OPTIONS,
    ...SECURED_COOKIE_OPTIONS(stage),
    expires: parseISO(customerUserAccessToken.expireAt)
  });
}
function setCredential(credential, stage, cookie = new Cookie()) {
  const { workspaceToken, customerUserAccessToken } = credential;
  const { accessToken, refreshToken } = workspaceToken;
  cookie.set(COOKIE_KEY_VERSION_IDENTIFIER, COOKIE_VALUE_V2_VERSION_IDENTIFIER, {
    ...DEFAULT_COOKIE_OPTIONS,
    expires: parseISO((refreshToken ?? accessToken).expireAt)
  });
  if (refreshToken) {
    cookie.set(COOKIE_KEY_WS_RID, refreshToken.token, {
      ...DEFAULT_COOKIE_OPTIONS,
      ...SECURED_COOKIE_OPTIONS(stage),
      expires: parseISO(refreshToken.expireAt)
    });
  }
  const wsAidExpireAt = parseISO(accessToken.expireAt);
  const aidExpireAt = parseISO(customerUserAccessToken.expireAt);
  const maxExpireAt = wsAidExpireAt > aidExpireAt ? wsAidExpireAt : aidExpireAt;
  cookie.set(COOKIE_KEY_WS_AID, accessToken.token, {
    ...DEFAULT_COOKIE_OPTIONS,
    ...SECURED_COOKIE_OPTIONS(stage),
    expires: maxExpireAt
  });
  cookie.remove(COOKIE_KEY_LOCALE, DEFAULT_COOKIE_OPTIONS);
  cookie.remove(COOKIE_KEY_LOGIN_ERROR_MESSAGE, DEFAULT_COOKIE_OPTIONS);
  setCustomerUserAccessToken(customerUserAccessToken, stage);
}
function getVersion(cookie = new Cookie()) {
  return cookie.get(COOKIE_KEY_VERSION_IDENTIFIER);
}
function setReauthToken(token, cookie = new Cookie()) {
  cookie.set(COOKIE_KEY_WS_FFA, token, DEFAULT_COOKIE_OPTIONS);
}
function getReauthToken(cookie = new Cookie()) {
  const reauthToken = cookie.get(COOKIE_KEY_WS_FFA) ?? "";
  return reauthToken;
}
function getCredential(cookie = new Cookie()) {
  const workspaceAccessToken = cookie.get(COOKIE_KEY_WS_AID) ?? "";
  const workspaceRefreshToken = cookie.get(COOKIE_KEY_WS_RID) ?? "";
  const customerUserAccessToken = cookie.get(COOKIE_KEY_AID) ?? "";
  return {
    workspaceToken: {
      accessToken: workspaceAccessToken,
      refreshToken: workspaceRefreshToken
    },
    customerUserAccessToken
  };
}
function getDeviceId(cookie = new Cookie()) {
  return cookie.get(COOKIE_KEY_DEVICE_ID);
}
function setDeviceId(deviceId, cookie = new Cookie()) {
  return cookie.set(COOKIE_KEY_DEVICE_ID, deviceId, {
    ...DEFAULT_COOKIE_OPTIONS,
    maxAge: COOKIE_MAX_AGE
  });
}
function getCurrentCustomerInfo(cookie = new Cookie()) {
  return cookie.get(COOKIE_KEY_CURRENT_CUSTOMER_INFO);
}
function setCurrentCustomerInfo(customer, cookie = new Cookie()) {
  return cookie.set(COOKIE_KEY_CURRENT_CUSTOMER_INFO, customer, {
    maxAge: COOKIE_MAX_AGE,
    ...DEFAULT_COOKIE_OPTIONS
  });
}
const invalidateCredential = async (cookie = new Cookie()) => {
  cookie.remove(COOKIE_KEY_VERSION_IDENTIFIER, DEFAULT_COOKIE_OPTIONS);
  cookie.remove(COOKIE_KEY_WS_AID, DEFAULT_COOKIE_OPTIONS);
  cookie.remove(COOKIE_KEY_WS_RID, DEFAULT_COOKIE_OPTIONS);
  cookie.remove(COOKIE_KEY_AID, DEFAULT_COOKIE_OPTIONS);
  cookie.remove(COOKIE_KEY_LOCALE, DEFAULT_COOKIE_OPTIONS);
  cookie.remove(COOKIE_KEY_CURRENT_CUSTOMER_INFO, DEFAULT_COOKIE_OPTIONS);
  cookie.remove(COOKIE_KEY_LOGIN_ERROR_MESSAGE, DEFAULT_COOKIE_OPTIONS);
  unregisterFCM();
  const DISCONNECT_TIMEOUT = 1e3;
  await Promise.race([
    disconnect(),
    new Promise((resolve) => {
      setTimeout(() => {
        console.warn("[services-auth] disconnectServerPushConsumer timeout");
        resolve();
      }, DISCONNECT_TIMEOUT);
    })
  ]);
};
async function clearClientSession() {
  try {
    try {
      await invalidateCredential();
      invalidateSessionStorage();
      invalidateLocalStorage();
      await deleteDB();
    } finally {
      broadcastUnauthorizedMessage();
    }
  } catch {
  }
}
async function redirectToLoginPage(_nextUrl) {
  const canBeLoop = window.location.pathname === LOGIN_PAGE_PATH;
  if (canBeLoop) {
    return;
  }
  const canNextUrlBeLoop = _nextUrl === LOGIN_PAGE_PATH;
  const nextUrl = canNextUrlBeLoop ? void 0 : _nextUrl;
  const redirectUrl = nextUrl !== void 0 ? `${LOGIN_PAGE_PATH}?nextUrl=${encodeURIComponent(nextUrl)}` : LOGIN_PAGE_PATH;
  setTimeout(() => window.location.replace(redirectUrl), 0);
}
async function clearClientSessionWithRedirect(_nextUrl) {
  await clearClientSession();
  redirectToLoginPage(_nextUrl);
}
async function revokeServerToken(cookie = new Cookie()) {
  const workspaceAccessToken = cookie.get(COOKIE_KEY_WS_AID) ?? "";
  const [logoutRemoteFn] = logoutRemote(globalAxios);
  if (workspaceAccessToken !== "") {
    try {
      const deviceId = getDeviceId(cookie);
      await logoutRemoteFn(
        { flexTeamV2WorkspaceAccess: workspaceAccessToken },
        {
          headers: {
            [HEADER_KEY_DEVICE_ID]: deviceId
          }
        }
      );
    } catch {
    }
  }
}
async function logout(nextUrl) {
  await revokeServerToken();
  clearClientSessionWithRedirect(nextUrl);
}

export { clearClientSession, clearClientSessionWithRedirect, getCredential, getCurrentCustomerInfo, getDeviceId, getReauthToken, getVersion, invalidateCredential, logout, redirectToLoginPage, revokeServerToken, setCredential, setCurrentCustomerInfo, setCustomerUserAccessToken, setDeviceId, setReauthToken };
//# sourceMappingURL=credentials.mjs.map
